/**
 * Cross-page back-navigation helper.
 *
 * Admin detail pages (Submission, Hint, ModelRun, …) link to each other
 * freely. Without a shared convention, each page invents its own back
 * handling and labels drift.
 *
 * Convention:
 *   - When navigating TO a detail page from another detail page, the
 *     caller stamps a `returnTo` entry on the React-Router state via
 *     `withReturnTo` (or its shorthand `navigateWithReturn`). The entry
 *     carries only a `label` — the actual back navigation uses
 *     `navigate(-1)` (browser back), which preserves history so chained
 *     drill-downs (A → B → C → back → back → back to A) work for free
 *     without any per-hop bookkeeping.
 *   - The destination page reads the label with `useReturnLocation`. The
 *     `<BackButton>` companion component pairs the label with
 *     `navigate(-1)` and falls back to a page-specific default URL when
 *     `returnTo` is absent (direct URL visits, bookmarks, refreshes).
 *
 * The optional `url` field is a safety net used as a hard-target if
 * history isn't navigable for some reason; in practice `navigate(-1)`
 * succeeds whenever `returnTo` was set.
 */
import { useLocation, type NavigateFunction, type To } from 'react-router-dom';

export interface ReturnLocation {
    /** Label shown on the back button (e.g. "Back to Hint Detail"). */
    label: string;
    /** Optional safety fallback URL. Browser history (`navigate(-1)`) is
     *  used first; this URL is only consulted if a caller explicitly
     *  prefers hard-navigation over history-pop. */
    url?: string;
}

/** Hook: read the incoming `returnTo` from router state. */
export function useReturnLocation(): ReturnLocation | undefined {
    const location = useLocation();
    return (location.state as { returnTo?: ReturnLocation } | null)?.returnTo;
}

/** Build the second argument for `navigate()` that carries a return hint.
 *  Use the `extraState` slot to merge additional router-state fields. */
export function withReturnTo(
    returnTo: ReturnLocation,
    extraState?: Record<string, unknown>,
): { state: { returnTo: ReturnLocation } & Record<string, unknown> } {
    return { state: { returnTo, ...extraState } };
}

/** Convenience: navigate AND attach a return hint in one call. */
export function navigateWithReturn(
    navigate: NavigateFunction,
    to: To,
    returnTo: ReturnLocation,
    extraState?: Record<string, unknown>,
): void {
    navigate(to, withReturnTo(returnTo, extraState));
}
